import { SelectOption } from 'components/shared/Select'
import {
  action,
  computed,
  observable,
  toJS
} from 'mobx'
import {
  BrandDTO,
  EventHighlightDTO,
  ViewerEventEnums
} from 'models'
import { BRANDS_MAIN_STORE } from 'store/BrandsMainStore'
import { GENERAL_STORE } from 'store/GeneralStore'
import { RootStore } from 'store/RootStore'
import { STATISTICS_OVERVIEW_STORE } from 'store/StatisticsOverviewStore'
import { Api, formatCirclesDuration, getToFixed2Number, User } from 'utilities'
import {
  EventHighlights,
  OrderProductStatsDTO,
  PieSegment,
  ShopHighlightTabs,
  StoreViewData
} from './StatisticsPageStore.types'
import { STATISTICS_OVERVIEW_BAR_CHART_STORE } from '../StatisticsOverviewBarChartStore'
import { STATISTICS_OVERVIEW_TABLE_STORE } from '../StatisticsOverviewTableStore'
import { STATISTICS_VIEWERS_TABLE_STORE } from '../StatisticsViewersTableStore'
import { STATISTICS_OVERVIEW_PERIOD_CHART_STORE } from 'store/StatisticsViewerPeriodChartStore'
import { parseMilliseconds } from './StatisticsPage.utils'
import moment from "moment"

export const STATISTICS_PAGE_STORE = 'STATISTICS_PAGE_STORE'

export class StatisticsPageStore {
  private _rootStore: RootStore

  constructor (rootStore: RootStore) {
    this._rootStore = rootStore
  }

  @observable private _orderProductStats: OrderProductStatsDTO = null
  @observable private _eventHightLights: EventHighlights = null
  @observable private _selectedHighlightTab: ShopHighlightTabs = ShopHighlightTabs.BY_REVENUE
  @observable selectedStoreId: number = null
  @observable private _selectedStoreIds: Set<number> = new Set()

  private updateStoresTimeout: NodeJS.Timeout = null

  @action private _setOrderProductStats = (orderProductStats: OrderProductStatsDTO) => {
    this._orderProductStats = orderProductStats
  }

  @action private _setEventHighlights = (eventHighlights: EventHighlights) => {
    this._eventHightLights = eventHighlights
  }

  @action setSelectedHighlightTab = (tab: ShopHighlightTabs) => {
    if (tab === ShopHighlightTabs.BY_EXPENSES) {
      tab = ShopHighlightTabs.BY_REVENUE
    }
    this._selectedHighlightTab = tab
  }

  @action setSelectedStoreId = (storeId: number) => {
    if (this.updateStoresTimeout) {
      clearTimeout(this.updateStoresTimeout)
    }
    const updateStores = () => {
      this._rootStore[STATISTICS_OVERVIEW_TABLE_STORE].init()
      if (!storeId) {
        this.selectedStoreId = null
        this._selectedStoreIds = new Set()
      } else {
        this.selectedStoreId = storeId
        this._selectedStoreIds.add(storeId)
        if (this._selectedStoreIds.size === 1) {
          this._rootStore[STATISTICS_OVERVIEW_TABLE_STORE].setSingleStoreView()
          this._rootStore[STATISTICS_VIEWERS_TABLE_STORE].setSingleStoreView()
        } else if (this._selectedStoreIds.size === 2) {
          this._rootStore[STATISTICS_OVERVIEW_TABLE_STORE].setMultiStoreView()
          this._rootStore[STATISTICS_VIEWERS_TABLE_STORE].setMultiStoreView()
        }
      }
      this._getStats()
      this._rootStore[STATISTICS_OVERVIEW_PERIOD_CHART_STORE].clearTotalPeriodDataMap()
      this._rootStore[STATISTICS_OVERVIEW_BAR_CHART_STORE].getEventsPeriodStats()
    }
    this.updateStoresTimeout = setTimeout(updateStores, 300)
  }

  @action excludeStore = (storeId: number) => {
    if (this.updateStoresTimeout) {
      clearTimeout(this.updateStoresTimeout)
    }
    const updateStores = () => {
      this._rootStore[STATISTICS_OVERVIEW_TABLE_STORE].init()
      this._selectedStoreIds.delete(storeId)
      if (this._selectedStoreIds.size === 0) {
        this.selectedStoreId = null
        this._rootStore[STATISTICS_OVERVIEW_TABLE_STORE].setMultiStoreView()
        this._rootStore[STATISTICS_VIEWERS_TABLE_STORE].setMultiStoreView()
      } else if (this._selectedStoreIds.size === 1) {
        this._rootStore[STATISTICS_OVERVIEW_TABLE_STORE].setSingleStoreView()
        this._rootStore[STATISTICS_VIEWERS_TABLE_STORE].setSingleStoreView()
      } else if (this._selectedStoreIds.size === 2) {
        this._rootStore[STATISTICS_OVERVIEW_TABLE_STORE].setMultiStoreView()
        this._rootStore[STATISTICS_VIEWERS_TABLE_STORE].setMultiStoreView()
      }
      this._getStats()
      this._rootStore[STATISTICS_OVERVIEW_BAR_CHART_STORE].getEventsPeriodStats()
    }
    this._rootStore[STATISTICS_OVERVIEW_PERIOD_CHART_STORE].clearTotalPeriodDataMap()
    this.updateStoresTimeout = setTimeout(updateStores, 300)
  }

  private _setStats = (data: [OrderProductStatsDTO, EventHighlights]) => {
    const [orderProductStats, eventHighlights] = data
    this._setOrderProductStats(orderProductStats)
    this._setEventHighlights(eventHighlights)
  }

  onStatisticsMainPageMount = () => {
    this._rootStore[BRANDS_MAIN_STORE]
      .getBrands()
      .then(() => {
        if (User.isShopOwner()) {
          if (this._rootStore[BRANDS_MAIN_STORE].brandsIds) {
            this.setSelectedStoreId(this._rootStore[BRANDS_MAIN_STORE].brandsIds[0])
          }
        } else {
          this._getStats()
        }
      })
  }

  public getHighlightStats = async (): Promise<void> => {
    await Promise.all([
      Api.post<OrderProductStatsDTO>('/order/statistic/highlights', {
        brands: this.selectedStoreIds
      }),
      Api.post<EventHighlights>('/stats/summary/highlights', {
        brandIds: this.selectedStoreIds
      }, 'statsApi')
    ])
      .then(this._setStats)
      .catch(this._rootStore[GENERAL_STORE].handleApiError)
  }

  private _getStats = async () => {
    const {
      [GENERAL_STORE]: {
        handleApiError
      },
      [STATISTICS_OVERVIEW_STORE]: {
        getProjectProductEvents
      }
    } = this._rootStore

    const updateStats = async () => {
      await this.getHighlightStats()
        .then(getProjectProductEvents)
        .catch(handleApiError)
    }
    await updateStats()
  }

  @computed get storesAsSelectOptions (): SelectOption<number>[] {
    const {
      brandsList
    } = this._rootStore[BRANDS_MAIN_STORE]

    return brandsList.map(brand => ({
      label: brand.name,
      value: brand.id
    }))
  }

  @computed get storeViewTabData (): StoreViewData {
    const showStoreName = !this.shopToDisplay
    const getGraphicsTotal = (segments: PieSegment[]): number => {
      return segments.reduce((sum, segment) => sum + segment.num, 0)
    }

    const calcAndApplyGraphicPercentages = (segments: PieSegment[], total: number) => {
      segments.forEach((segment, index) => {
        segment.percentage = (segment.num / total || 0) * 100
        const prevSegment = index === 0
          ? null
          : segments[index - 1]
        segment.percentageSkip = prevSegment
          ? prevSegment.percentageSkip + prevSegment.percentage
          : 0
      })
    }

    const getStoreViewDataForByRevenueTab = (): StoreViewData => {
      const getPieSegmentByEventType = (eventType: ViewerEventEnums): PieSegment => {
        const label = {
          [ViewerEventEnums.BLOB_TRIGGERED]: 'BLOBS',
          [ViewerEventEnums.PRODUCT_PAGE_OPENED]: 'PRODUCT PAGES',
          [ViewerEventEnums.POSTER_CLICKED]: 'POSTERS',
          [ViewerEventEnums.ADD_TO_CART]: 'ADDED TO CART',
          [ViewerEventEnums.VOV_CLICKED]: 'VOV',
          [ViewerEventEnums.PURCHASE]: 'PURCHASE FEE'
        }[eventType]


        if (ViewerEventEnums.BLOB_TRIGGERED === eventType) {

          const eventHighlightData: EventHighlightDTO = this._eventHightLights
          ? this._eventHightLights[eventType] || {} as any
          : {} as any

          const eventBlobTriggeredHighlightRevenue =
          eventHighlightData.revenue || 0

          const eventBlobAttachedHighlightRevenue =
          this._eventHightLights?.blobAttached?.revenue || 0

          const eventBlobDetachedHighlightRevenue =
          this._eventHightLights?.blobDetached?.revenue || 0

          const blobsRevenue = 
          eventBlobAttachedHighlightRevenue + 
          eventBlobDetachedHighlightRevenue +
          eventBlobTriggeredHighlightRevenue

        return {
          label,
          num: blobsRevenue,
          isPrice: true,
          deltaPercentage: getToFixed2Number(
            (eventHighlightData.percentageDeltaRevenue || 0) * 100
          ),
          percentage: 0,
          bestItem: eventHighlightData.bestBlobTotalRevenue
            ? {
              productId: eventHighlightData.bestBlobTotalRevenue.productId,
              isPrice: true,
              productStatNumber: eventHighlightData?.bestBlobTotalRevenue?.revenue,
              afterStatNumberLabel: 'revenue',
              showStoreName
            }
            : null
        }
        }

        if (ViewerEventEnums.POSTER_CLICKED === eventType) {

          const eventHighlightData: EventHighlightDTO = this._eventHightLights
          ? this._eventHightLights[eventType] || {} as any
          : {} as any

          const eventPosterClickedHighlightRevenue =
          eventHighlightData.revenue || 0

          const eventPosterAttachedHighlightRevenue =
          this._eventHightLights?.posterAttached?.revenue || 0

          const postersRevenue = 
          eventPosterClickedHighlightRevenue +
          eventPosterAttachedHighlightRevenue

        return {
          label,
          num: postersRevenue,
          isPrice: true,
          deltaPercentage: getToFixed2Number(
            (eventHighlightData.percentageDeltaRevenue || 0) * 100
          ),
          percentage: 0,
          bestItem: eventHighlightData.bestPosterRevenue
            ? {
              productId: eventHighlightData.bestPosterRevenue.productId,
              isPrice: true,
              productStatNumber: eventHighlightData.bestPosterRevenue.revenue,
              afterStatNumberLabel: 'revenue',
              showStoreName
            }
            : null
        }
        }

        if (ViewerEventEnums.PURCHASE === eventType) {
          const {
            productId,
            sales
          } = (this._orderProductStats || {}).topCommission || {}
          return {
            label,
            num: this._orderProductStats ? this._orderProductStats.totalCommission : 0,
            isPrice: true,
            deltaPercentage: getToFixed2Number(
              (this._orderProductStats ? this._orderProductStats.deltaCommission : 0) * 100
            ),
            percentage: 0,
            bestItem: {
              productId,
              productStatNumber: sales ? sales.commission : 0,
              isPrice: true,
              afterStatNumberLabel: 'revenue',
              showStoreName
            }
          }
        }

        if (ViewerEventEnums.ADD_TO_CART === eventType) {

          const eventHighlightData: EventHighlightDTO = this._eventHightLights
          ? this._eventHightLights[eventType] || {} as any
          : {} as any

          return {
            label,
            num: eventHighlightData.revenue || 0,
            isPrice: true,
            deltaPercentage: getToFixed2Number(
              (eventHighlightData.percentageDeltaRevenue || 0) * 100
            ),
            percentage: 0,
            bestItem: eventHighlightData.bestAdRevenue
              ? {
                productId: eventHighlightData.bestAdRevenue.productId,
                isPrice: true,
                productStatNumber: eventHighlightData.bestAdRevenue.revenue,
                afterStatNumberLabel: 'revenue',
                showStoreName
              }
              : null
          }
        }

        const eventHighlightData: EventHighlightDTO = this._eventHightLights
          ? this._eventHightLights[eventType] || {} as any
          : {} as any

        return {
          label,
          num: eventHighlightData.revenue || 0,
          isPrice: true,
          deltaPercentage: getToFixed2Number(
            (eventHighlightData.percentageDeltaRevenue || 0) * 100
          ),
          percentage: 0,
          bestItem: eventHighlightData.bestRevenue
            ? {
              productId: eventHighlightData.bestRevenue.productId,
              isPrice: true,
              productStatNumber: eventHighlightData.bestRevenue.revenue,
              afterStatNumberLabel: 'revenue',
              showStoreName
            }
            : null
        }
      }

      const pieSegments: PieSegment[] = [
        getPieSegmentByEventType(ViewerEventEnums.BLOB_TRIGGERED),
        getPieSegmentByEventType(ViewerEventEnums.PRODUCT_PAGE_OPENED),
        getPieSegmentByEventType(ViewerEventEnums.POSTER_CLICKED),
        getPieSegmentByEventType(ViewerEventEnums.ADD_TO_CART),
        getPieSegmentByEventType(ViewerEventEnums.PURCHASE),
        getPieSegmentByEventType(ViewerEventEnums.VOV_CLICKED)
      ]

      const total = getGraphicsTotal(pieSegments)
      calcAndApplyGraphicPercentages(pieSegments, total)
      return {
        total,
        isTotalPrice: true,
        pieSegments
      }
    }

    switch (this._selectedHighlightTab) {
      case (ShopHighlightTabs.BY_REVENUE): {
        return getStoreViewDataForByRevenueTab()
      }

      case (ShopHighlightTabs.BY_INTERACTIONS): {
        const getPieSegmentByEventType = (eventType: ViewerEventEnums): PieSegment => {
          const label = {
            [ViewerEventEnums.BLOB_TRIGGERED]: 'BLOBS TRIGGERED',
            [ViewerEventEnums.PRODUCT_PAGE_OPENED]: 'PRODUCT PAGES OPENED',
            [ViewerEventEnums.POSTER_CLICKED]: 'POSTERS CLICKED',
            [ViewerEventEnums.POSTER_ATTACHED]: 'POSTER ATTACHED',
            [ViewerEventEnums.ADD_TO_CART]: 'ADDED TO CART',
            [ViewerEventEnums.PURCHASE]: 'PURCHASES',
            [ViewerEventEnums.VOV_CLICKED]: 'VOV CLICKED'
          }[eventType] || eventType

          if (ViewerEventEnums.PURCHASE === eventType) {
            const {
              productId,
              sales
            } = (this._orderProductStats || {}).topOrdered || {}
            return {
              label,
              num: this._orderProductStats ? this._orderProductStats.purchases : 0,
              deltaPercentage: getToFixed2Number(
                (this._orderProductStats ? this._orderProductStats.deltaPurchases : 0) * 100
              ),
              percentage: 0,
              bestItem: {
                productId,
                productStatNumber: sales ? sales.count : 0,
                afterStatNumberLabel: 'purchase',
                showStoreName
              }
            }
          }

          if (ViewerEventEnums.ADD_TO_CART === eventType) {
            return {
              label,
              num: this._eventHightLights?.productAddedToCard ? this._eventHightLights.productAddedToCard.eventCount : 0,
              deltaPercentage: getToFixed2Number(
                (this._orderProductStats ? this._eventHightLights.productAddedToCard?.percentageDeltaCount : 0) * 100
              ) || 0,
              percentage: 0,
              bestItem: {
                productId: this._eventHightLights?.productAddedToCard?.bestCount?.productId,
                productStatNumber: this._eventHightLights.productAddedToCard ? this._eventHightLights.productAddedToCard?.bestCount?.eventCount : 0,
                afterStatNumberLabel: 'added to cart',
                showStoreName
              }
            }
          }

          if (ViewerEventEnums.POSTER_ATTACHED === eventType) {
            const eventHighlightData: EventHighlightDTO = this._eventHightLights
            ? this._eventHightLights[eventType] || {} as any
            : {} as any

            const {
              label,
              value,
            } = parseMilliseconds(eventHighlightData.totalPosterAttachedTime)
            const secondsDuration = parseMilliseconds(eventHighlightData?.bestDuration?.duration)
            
          return {
            label: 'Total posters "UP" time',
            num: value,
            customFormattedTime: formatCirclesDuration(eventHighlightData?.totalPosterAttachedTime),
            afterNumLabel: label,
            deltaPercentage: getToFixed2Number(
              (eventHighlightData?.percentageDeltaCount || 0) * 100
            ),
            percentage: 0,
            bestItem: eventHighlightData?.bestDuration
              ? {
                productId: eventHighlightData.bestDuration?.productId,
                productStatNumber: secondsDuration.value,
                afterStatNumberLabel: secondsDuration.label,
                showStoreName
              }
              : null
          }
          }

          const eventHighlightData: EventHighlightDTO = this._eventHightLights
            ? this._eventHightLights[eventType] || {} as any
            : {} as any

          return {
            label,
            num: eventHighlightData.eventCount || 0,
            afterNumLabel: 'times',
            deltaPercentage: getToFixed2Number(
              (eventHighlightData.percentageDeltaCount || 0) * 100
            ),
            percentage: 0,
            bestItem: eventHighlightData.bestCount
              ? {
                productId: eventHighlightData.bestCount.productId,
                productStatNumber: eventHighlightData.bestCount.eventCount,
                afterStatNumberLabel: 'times',
                showStoreName
              }
              : null
          }
        }

        const pieSegments: PieSegment[] = [
          getPieSegmentByEventType(ViewerEventEnums.BLOB_TRIGGERED),
          getPieSegmentByEventType(ViewerEventEnums.PRODUCT_PAGE_OPENED),
          getPieSegmentByEventType(ViewerEventEnums.POSTER_CLICKED),
          getPieSegmentByEventType(ViewerEventEnums.POSTER_ATTACHED),
          getPieSegmentByEventType(ViewerEventEnums.ADD_TO_CART),
          getPieSegmentByEventType(ViewerEventEnums.PURCHASE),
          getPieSegmentByEventType(ViewerEventEnums.VOV_CLICKED)
        ]

        const total = getGraphicsTotal(pieSegments)
        calcAndApplyGraphicPercentages(pieSegments, total)
        return {
          total,
          afterTotalLabel: 'interactions',
          pieSegments
        }
      }

      case (ShopHighlightTabs.BY_VIEWERS): {
        const getPieSegmentByEventType = (eventType: ViewerEventEnums): PieSegment => {
          const eventHighlightData: EventHighlightDTO = this._eventHightLights
            ? this._eventHightLights[eventType] || {} as any
            : {} as any

          switch (eventType) {
            case (ViewerEventEnums.VIDEO_STOPPED): {
              const {
                label,
                value,
              } = parseMilliseconds(eventHighlightData.totalWatchTime)

              return {
                label: 'TOTAL WATCH TIME',
                num: value,
                customFormattedTime: formatCirclesDuration(eventHighlightData.totalWatchTime),
                afterNumLabel: label,
                deltaPercentage: getToFixed2Number(
                  (eventHighlightData.percentageDeltaDuration || 0) * 100
                ),
                percentage: 0
              }
            }
            case (ViewerEventEnums.INTERACTION_STOPPED): {
              const {
                label,
                value,
              } = parseMilliseconds(eventHighlightData.totalInteractionTime)

              return {
                label: 'TOTAL VIDEO INTERACTION TIME',
                num: value,
                customFormattedTime: formatCirclesDuration(eventHighlightData.totalInteractionTime),
                afterNumLabel: label,
                deltaPercentage: getToFixed2Number(
                  (eventHighlightData.percentageDeltaDuration || 0) * 100
                ),
                percentage: 0
              }
            }
            case (ViewerEventEnums.PAGE_INTERACTION_STOPPED): {
              const {
                label,
                value,
              } = parseMilliseconds(eventHighlightData.totalPageInteractionTime)

              return {
                label: 'TOTAL PAGE INTERACTION TIME',
                num: value,
                customFormattedTime: formatCirclesDuration(eventHighlightData.totalPageInteractionTime),
                afterNumLabel: label,
                deltaPercentage: getToFixed2Number(
                  (eventHighlightData.percentageDeltaDuration || 0) * 100
                ),
                percentage: 0
              }
            }
            case (ViewerEventEnums.PURCHASE): {
              const {
                productId,
                sales
              } = (this._orderProductStats || {}).topRevenue || {}
              return {
                label: 'PURCHASE REVENUE',
                num: this._orderProductStats ? this._orderProductStats.totalRevenue : 0,
                isPrice: true,
                deltaPercentage: getToFixed2Number(
                  (this._orderProductStats ? this._orderProductStats.deltaRevenue : 0) * 100
                ),
                percentage: 0,
                bestItem: {
                  productId,
                  productStatNumber: sales ? sales.total : 0,
                  isPrice: true,
                  afterStatNumberLabel: 'revenue',
                  showStoreName
                }
              }
            }
            default: return {
              label: eventType,
              num: 0,
              deltaPercentage: 0,
              percentage: 0
            }
          }
        }

        const getAdRevenueSegment = (): PieSegment => {
          const {
            total,
            isTotalPrice,
            pieSegments
          } = getStoreViewDataForByRevenueTab()
          const delta = pieSegments.reduce(
            (sum, segment) => sum + (segment.num * (segment.percentage / 100)),
            0
          ) / total || 0
          const bestItem = pieSegments.reduce(
            (bestImt, segment) => {
              if (!bestImt) return segment.bestItem
              if (!segment.bestItem) return bestImt
              return bestImt.productStatNumber >= segment.bestItem.productStatNumber
                ? bestImt
                : segment.bestItem
            }, null as PieSegment['bestItem']
          )

          return {
            label: User.isShopOwner() ? 'AD EXPENSES' : 'AD REVENUE',
            num: total,
            isPrice: isTotalPrice,
            deltaPercentage: getToFixed2Number(delta * 100),
            percentage: 0,
            bestItem
          }
        }

        const pieSegments: PieSegment[] = [
          getPieSegmentByEventType(ViewerEventEnums.VIDEO_STOPPED),
          getPieSegmentByEventType(ViewerEventEnums.PAGE_INTERACTION_STOPPED),
          getPieSegmentByEventType(ViewerEventEnums.INTERACTION_STOPPED),
          getAdRevenueSegment(),
          getPieSegmentByEventType(ViewerEventEnums.PURCHASE)
        ]

        return {
          total: undefined,
          pieSegments
        }
      }

      default: return {
        total: 0,
        pieSegments: []
      }
    }
  }

  @computed get selectedStores (): BrandDTO[] {
    const {
      getBrandById
    } = this._rootStore[BRANDS_MAIN_STORE]

    return Array.from(this._selectedStoreIds.values())
      .map(getBrandById)
  }

  @computed get shopToDisplay (): BrandDTO {
    if (this.selectedStores.length !== 1) return null
    return this.selectedStores[0]
  }

  @computed get selectedStoreIds (): number[] {
    return Array.from(this._selectedStoreIds.values())
  }

  getHighlightTabs (): ShopHighlightTabs[] {
    return [
      User.isShopOwner() ? ShopHighlightTabs.BY_EXPENSES : ShopHighlightTabs.BY_REVENUE,
      ShopHighlightTabs.BY_INTERACTIONS, ShopHighlightTabs.BY_VIEWERS
    ]
  }
}
